import { ThinkingOrb, type OrbSize, type OrbState } from "thinking-orbs";

import { cx } from "@/lib/cx";

import styles from "./LoadingOrb.module.css";

/** What the app is waiting on. Each maps to one orb animation, so call sites never name the library's states. */
export type LoadingActivity = "loading" | "connecting" | "searching" | "opening" | "working";

/** `page` for a wait that fills the screen or a card, `inline` beside text or in a list. */
export type LoadingOrbSize = "inline" | "page";

const ORB_STATE: Record<LoadingActivity, OrbState> = {
  loading: "breathing",
  connecting: "connecting",
  searching: "searching",
  opening: "solving",
  working: "working",
};

/** The library's two hand-tuned sizes, in CSS pixels; other sizes are interpolated and look softer. */
const ORB_SIZE: Record<LoadingOrbSize, OrbSize> = { inline: 20, page: 64 };

export interface LoadingOrbProps {
  /** Accessible text; omit when a parent already announces the busy state (a region with role="status"). */
  label?: string;
  activity?: LoadingActivity;
  size?: LoadingOrbSize;
  className?: string | undefined;
}

/**
 * Animated wait indicator (thinking-orbs, MIT). It follows the light or dark theme on <html>, shows a still frame
 * under reduced motion, and pauses while off screen or in a hidden tab. Buttons keep the smaller `Spinner`.
 */
export function LoadingOrb({
  label,
  activity = "loading",
  size = "inline",
  className,
}: LoadingOrbProps) {
  return (
    <span className={cx(styles.orb, className)} role={label === undefined ? undefined : "status"}>
      <ThinkingOrb state={ORB_STATE[activity]} size={ORB_SIZE[size]} aria-hidden="true" />
      {label !== undefined && <span className="sr-only">{label}</span>}
    </span>
  );
}
